/**
 * KPIs acionáveis da fila do comprador (ADR-0069, D8). Usa as classes
 * canônicas `.kpi-strip-v2` (mesma anatomia do KpiStrip) e acrescenta o
 * que o mockup pede: borda superior na cor do indicador e delta. Cada
 * cartão é um botão que aplica o filtro correspondente.
 */
import React from "react";

import { formatarDataLocal } from "../../../utils/dinheiro";

function fmtDias(v) {
  if (v === null || v === undefined) return "—";
  return String(v).replace(".", ",");
}

function fmtMil(valor) {
  const n = Number(valor || 0);
  if (n >= 1000) return { valor: (n / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 }), unidade: "mil" };
  return { valor: n.toLocaleString("pt-BR", { maximumFractionDigits: 0 }), unidade: "" };
}

function diaSemanaCurto(iso) {
  if (!iso) return "";
  const [a, m, d] = iso.split("-").map(Number);
  const dt = new Date(a, m - 1, d);
  return `${dt.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "")} ${formatarDataLocal(iso).slice(0, 5)}`;
}

export default function KpisFila({ resumo, ativo, onFiltrar }) {
  const r = resumo || {};
  const emAberto = fmtMil(r.valor_em_aberto);
  const deltaAtr = Number(r.atrasadas_delta_ontem || 0);
  const deltaTempo = r.tempo_cotar_delta_mes === null || r.tempo_cotar_delta_mes === undefined
    ? null : Number(r.tempo_cotar_delta_mes);

  const cards = [
    {
      id: "atrasadas", label: "Atrasadas", valor: r.atrasadas ?? "—", cor: "var(--danger-solid)",
      corValor: "var(--danger-text)",
      sub: r.vencem_hoje ? `${r.vencem_hoje} vencem hoje` : "nenhuma vence hoje",
      delta: deltaAtr ? { texto: `${deltaAtr > 0 ? "+" : ""}${deltaAtr} vs ontem`, tom: deltaAtr > 0 ? "down" : "up" } : null,
    },
    {
      id: "aguardando", label: "Aguard. aprovação", valor: r.aguardando_aprovacao ?? "—", cor: "var(--warning-solid)",
      sub: r.aguardando_mais_antiga_dias ? `mais antiga: ${r.aguardando_mais_antiga_dias} dias` : "nenhuma parada",
    },
    {
      id: "tempo", label: "Tempo p/ cotar", valor: fmtDias(r.tempo_cotar_dias), unidade: "dias", cor: "var(--info-solid)",
      sub: `meta ${fmtDias(r.tempo_cotar_meta_dias || 3)} dias`,
      delta: deltaTempo !== null ? { texto: `${deltaTempo > 0 ? "+" : "−"}${fmtDias(Math.abs(deltaTempo))} vs mês`, tom: deltaTempo > 0 ? "down" : "up" } : null,
      semFiltro: true,
    },
    {
      id: "sem_anexo", label: "Sem anexo", valor: r.sem_anexo_itens ?? "—", unidade: "itens", cor: "var(--warning-solid)",
      sub: "terceiros bloqueados",
    },
    {
      id: "em_aberto", label: "Em aberto", valor: `R$ ${emAberto.valor}`, unidade: emAberto.unidade, cor: "var(--accent)",
      sub: `${r.requisicoes_cotadas ?? 0} requisições cotadas`, semFiltro: true,
    },
    {
      id: "entregas", label: "Entregas na semana", valor: r.entregas_semana ?? "—", cor: "var(--teal-solid)",
      sub: r.entregas_proxima ? `próx.: ${diaSemanaCurto(r.entregas_proxima)}` : "nenhuma prevista",
    },
  ];

  return (
    <div className="kpi-strip-v2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(136px, 1fr))", marginBottom: 0 }}>
      {cards.map((c) => {
        const clicavel = !c.semFiltro && Boolean(onFiltrar);
        const on = ativo === c.id;
        return (
          <div
            key={c.id}
            className="kpi-v2"
            role={clicavel ? "button" : "status"}
            tabIndex={clicavel ? 0 : undefined}
            onClick={clicavel ? () => onFiltrar(on ? null : c.id) : undefined}
            onKeyDown={clicavel ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onFiltrar(on ? null : c.id); } } : undefined}
            aria-pressed={clicavel ? on : undefined}
            style={{
              borderTop: `2px solid ${c.cor}`,
              padding: "10px 16px 12px",
              gap: 2,
              cursor: clicavel ? "pointer" : "default",
              background: on ? "var(--accent-soft)" : undefined,
              transition: "background .15s ease",
            }}
            title={clicavel ? (on ? "Clique para tirar o filtro" : "Clique para filtrar") : undefined}
          >
            <div className="lbl" style={{ whiteSpace: "normal", fontFamily: "var(--font-body)", fontWeight: 600 }}>{c.label}</div>
            <div className="val" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15, color: c.corValor || undefined }}>
              {c.valor}
              {c.unidade ? <span className="unit">{c.unidade}</span> : null}
            </div>
            <div className="meta" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: 12 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{c.sub}</span>
              {c.delta ? <span className={`delta ${c.delta.tom}`} style={{ marginLeft: 6, flexShrink: 0 }}>{c.delta.texto}</span> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
